import { useTranslation } from 'react-i18next';
import { useCollegaWeek } from '../hooks/useCollegaWeek';
import { useOwnWeek } from '../hooks/useOwnWeek';
import { useTrend } from '../hooks/useTrend';
import { TrendContext } from '../trend-context';
import { STORING_MISLUKT, STORING_ONBEREIKBAAR, vorigeWeekVan, weektoestand } from '../toestand';
import { GeenRapport, Geweigerd, Storing } from './MeasurementState';
import { PeriodPicker } from './PeriodPicker';
import { Rapportkaart } from './Rapportkaart';
import { useMeldToestand } from './Toestandsmelding';
import { WeekMaandTabs } from './WeekMaandTabs';

export interface FeedbackRapportProps {
  /** Wiens rapport: het eigen, of dat van één collega (het Zo-ziet-kader op de medewerkerpagina). */
  bron: { soort: 'eigen' } | { soort: 'collega'; userId: number };
  /** De gekozen week; die en de weken komen van de pagina, dit component kent geen adres. */
  week: string;
  weken: readonly string[];
  /** Zonder deze functie geen Weekkiezer: story 11 zet hem in de Medewerkerkop. */
  onKiesWeek?: (week: string) => void;
  /** Het niveau van de koppen op het hoogste niveau; 3 als de pagina zelf een `h2` erboven heeft. */
  kopNiveau?: 2 | 3 | 4;
  /** De naam van de tablist Week/Maand; standaard `qa.eigen.tabLijstAria`. */
  tabsLabel?: string;
}

/** Twee skeletblokken in de vorm van de twee Resultaatblokken, in een gebied dat zegt dat het laadt. */
function RapportLaden() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.eigen.laden'));
  return (
    <div aria-busy="true" data-testid="werkkwaliteit-laden" role="status">
      <span className="sr-only">{t('qa.eigen.laden')}</span>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div aria-hidden="true" className="h-56 rounded-[12px] bg-[var(--background-muted)]" data-werkkwaliteit-skelet="blok" />
        <div aria-hidden="true" className="h-56 rounded-[12px] bg-[var(--background-muted)]" data-werkkwaliteit-skelet="blok" />
      </div>
    </div>
  );
}

/**
 * "Mijn feedback" als één scherm voor de eigen week en die van een collega (SPEC-werkkwaliteit-herontwerp
 * story 9). De tabs Week en Maand en de Weekkiezer blijven altijd staan; laden, een storing, een weigering of
 * een week zonder meting vervangen alleen de inhoud van tab Week.
 *
 * Een storing leest nooit als een lege week (AD-25, AD-27). Ook voor een collega: de leidinggevende ziet
 * precies wat de medewerker ziet, in de tweede persoon (EXPERIENCE.md S2). Voor een
 * collega gaat er geen verzoek naar de eigen week, en andersom.
 */
export function FeedbackRapport({ bron, week, weken, onKiesWeek, kopNiveau = 2, tabsLabel }: FeedbackRapportProps) {
  const collega = bron.soort === 'collega' ? bron.userId : null;
  const eigen = useOwnWeek(collega === null ? week : '');
  const vanCollega = useCollegaWeek(collega, collega === null ? '' : week);
  const { data, isLoading, isError, isGeweigerd, refetch } = collega === null ? eigen : vanCollega;
  // Eén overzicht over de laatste vier afgesloten weken, los van de gekozen week (CAP-6).
  const trend = useTrend(bron);
  const vorigeWeek = vorigeWeekVan(weken, week);

  function inhoud() {
    if (weken.length === 0) return null;
    if (isLoading) return <RapportLaden />;
    if (isGeweigerd) return <Geweigerd />;
    if (isError || data === undefined) {
      return <Storing onHerlaad={() => void refetch()} oorzaak={STORING_ONBEREIKBAAR} vorigeWeek={vorigeWeek} />;
    }
    if (data.data === null) {
      return weektoestand(data.reden) === 'nog_niet_gemeten' ? (
        <GeenRapport
          laatsteGeslaagdeRun={data.laatsteGeslaagdeRun}
          metingIngeschakeld={data.metingIngeschakeld ?? null}
          volgendeRun={data.volgendeRun ?? null}
        />
      ) : (
        <Storing
          fout={data.fout ?? null}
          laatsteGeslaagdeRun={data.laatsteGeslaagdeRun}
          laatsteGeslaagdeWeek={data.laatsteGeslaagdeWeek ?? null}
          oorzaak={STORING_MISLUKT}
          onHerlaad={() => void refetch()}
          vorigeWeek={vorigeWeek}
          week={week}
          weken={weken}
        />
      );
    }
    return (
      <TrendContext.Provider value={trend}>
        <Rapportkaart kopNiveau={kopNiveau} week={data.data} />
      </TrendContext.Provider>
    );
  }

  return (
    <WeekMaandTabs
      bron={bron}
      kopNiveau={kopNiveau}
      tabsLabel={tabsLabel}
      week={
        <>
          {onKiesWeek && <PeriodPicker onKies={onKiesWeek} waarde={week} weken={weken} />}
          {inhoud()}
        </>
      }
    />
  );
}
